import React, { FormEvent, useId, useMemo, useState } from 'react';
import useRecaptcha from '../../hooks/useRecaptcha';
import './styles.css';

function Contact() {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState<string | null>(null);

  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const EMAILJS_PRIVATE_KEY = process.env.REACT_APP_EMAILJS_PRIVATE_KEY;

  const recaptchaId = useId();
  const recaptchaElementId = useMemo(
    () => `contact-recaptcha-${recaptchaId.replace(/:/g, '')}`,
    [recaptchaId],
  );

  const {
    status: recaptchaStatus,
    error: recaptchaError,
    execute,
  } = useRecaptcha(RECAPTCHA_SITE_KEY, recaptchaElementId);

  const isConfigured = useMemo(
    () => Boolean(EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY),
    [EMAILJS_PUBLIC_KEY, EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID],
  );
  const isSubmitting = status === 'submitting';
  const isRecaptchaReady = recaptchaStatus === 'ready';
  const isDisabled = isSubmitting || !isRecaptchaReady || !isConfigured;

  const updateField =
    (key: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    if (!isConfigured) {
      setStatus('error');
      setFeedback('Contact form is not configured yet. Please add EmailJS keys.');
      return;
    }

    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

    if (!name || !email || !message) {
      setStatus('error');
      setFeedback('Name, email, and message are required.');
      return;
    }

    if (!emailPattern.test(email)) {
      setStatus('error');
      setFeedback('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');

    try {
      const recaptchaToken = await execute();

      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: EMAILJS_SERVICE_ID,
          template_id: EMAILJS_TEMPLATE_ID,
          user_id: EMAILJS_PUBLIC_KEY,
          ...(EMAILJS_PRIVATE_KEY ? { accessToken: EMAILJS_PRIVATE_KEY } : {}),
          template_params: {
            from_name: name,
            from_email: email,
            phone,
            message,
            'g-recaptcha-response': recaptchaToken,
          },
        }),
      });

      if (!response.ok) {
        let errorText: string | undefined;
        try {
          const data = await response.json();
          errorText = data?.message || data?.error;
        } catch {
          errorText = await response.text();
        }
        throw new Error(errorText || 'Unable to send message right now.');
      }

      setStatus('success');
      setFeedback('Message sent! We will get back to you shortly.');
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'Unable to send your message.';
      setStatus('error');
      setFeedback(messageText);
    }
  };

  return (
    <section className="contact scroll-reveal" id="contact">
      <div className="contact-header">
        <h2>Contact us</h2>
      </div>
      <div className="contact-grid">
        <form className="contact-card contact-form" onSubmit={handleSubmit} noValidate>
          <div className="contact-card-header">Get in touch</div>
          <div className="contact-divider" aria-hidden="true" />
          {feedback && (
            <div
              className={`contact-banner ${status === 'success' ? 'contact-success' : 'contact-error'}`}
              role={status === 'success' ? 'status' : 'alert'}
            >
              {feedback}
            </div>
          )}
          {recaptchaError && (
            <div className="contact-banner contact-error" role="alert">
              {recaptchaError}
            </div>
          )}
          <div className="contact-row">
            <label className="contact-field" htmlFor="contact-name">
              <span>Name</span>
              <input
                id="contact-name"
                name="name"
                type="text"
                placeholder="Enter your name"
                value={form.name}
                onChange={updateField('name')}
                required
                autoComplete="name"
              />
            </label>
            <label className="contact-field" htmlFor="contact-phone">
              <span>Phone number</span>
              <input
                id="contact-phone"
                name="phone"
                type="tel"
                placeholder="Enter your phone number"
                value={form.phone}
                onChange={updateField('phone')}
                autoComplete="tel"
              />
            </label>
          </div>
          <label className="contact-field" htmlFor="contact-email">
            <span>Email</span>
            <input
              id="contact-email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={form.email}
              onChange={updateField('email')}
              required
              autoComplete="email"
            />
          </label>
          <label className="contact-field" htmlFor="contact-message">
            <span>Your message</span>
            <textarea
              id="contact-message"
              name="message"
              rows={5}
              placeholder="Tell us what you need"
              value={form.message}
              onChange={updateField('message')}
              required
            />
          </label>
          <button
            className="btn contact-submit"
            type="submit"
            disabled={isDisabled}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send message'}
          </button>
          <div id={recaptchaElementId} className="contact-recaptcha-holder" aria-hidden="true" />
        </form>
        <div className="contact-side">
          <div className="contact-card">
            <div className="contact-card-header">Contact information</div>
            <div className="contact-divider" aria-hidden="true" />
            <div className="contact-info">
              <div className="contact-info-item">
                <span className="contact-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img">
                    <path
                      d="M6.6 10.8c1.4 2.9 3.7 5.2 6.6 6.6l2.2-2.2c.3-.3.8-.4 1.2-.3 1 .3 2 .5 3.1.5.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.5 21 3 13.5 3 4c0-.6.4-1 1-1h2.6c.6 0 1 .4 1 1 0 1 .2 2.1.5 3.1.1.4 0 .9-.3 1.2l-2.2 2.2z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <div>
                  <div className="contact-info-label">Phone</div>
                  <div className="contact-info-value">097417-67862</div>
                </div>
              </div>
              <div className="contact-info-item">
                <span className="contact-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img">
                    <path
                      d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <div>
                  <div className="contact-info-label">Email</div>
                  <div className="contact-info-value">mdfaizan@frfisheries.com</div>
                </div>
              </div>
              <div className="contact-info-item">
                <span className="contact-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img">
                    <path
                      d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <div>
                  <div className="contact-info-label">Address</div>
                  <div className="contact-info-value">
                    100, 8th Cross Rd, Old Madiwala, Chikka Madivala, 1st Stage, BTM 1st Stage,
                    Bengaluru, Karnataka 560068
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-card">
            <div className="contact-card-header">Business hours</div>
            <div className="contact-divider" aria-hidden="true" />
            <div className="contact-hours">
              <div>
                <div className="contact-info-label">Monday - Friday</div>
                <div className="contact-info-value">9:00 am - 8:00 pm</div>
              </div>
              <div>
                <div className="contact-info-label">Saturday</div>
                <div className="contact-info-value">9:00 am - 6:00 pm</div>
              </div>
              <div>
                <div className="contact-info-label">Sunday</div>
                <div className="contact-info-value">9:00 am - 5:00 pm</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*<div className="contact-map" aria-hidden="true">
        <div className="contact-map-marker" />
      </div>*/}
    </section>
  );
}

export default Contact;
